/**
 * FileCollaborativeEditor - edit a Word file in Files with the shared document
 * editor (F-DMS edit + collaborate).
 *
 * The same editor Drafting uses (Tiptap; Yjs co-editing over Web PubSub when
 * COLLAB_EDITING_ENABLED is on), around three things a stored file needs that
 * a draft doesn't:
 *
 * - THE WARNING. The editor works in HTML, so a Word file is converted to HTML
 *   to edit and back to Word to save. Complex formatting can be lost (tracked
 *   changes, fields, headers/footers, some styles). The reader is told the
 *   first time, and every save is a new version they can restore.
 * - ACCESS. People who can only view the file get the editor read-only.
 * - CONFLICTS. Until live co-editing is on, two people can have the file open.
 *   Each save carries the version it started from; if someone saved since,
 *   the save is refused and the editor goes read-only with "reload to see
 *   their changes" - never a silent overwrite.
 */

import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiInfoLight, PiXLight } from 'react-icons/pi';
import CollaborativeDocumentEditor from '../../shared/CollaborativeDocumentEditor';
import { COLLAB_EDITING_ENABLED } from '../../Drafting/draftingConfig';
import { htmlToDocx, blobToBase64 } from '../../../utils/docxHandler';

export const FILE_EDIT_WARNING_KEY = 'pons.files.editWarningSeen';

export class FileChangedError extends Error {
  code = 'FILE_CHANGED' as const;
  constructor() { super('FILE_CHANGED'); }
}

interface Props {
  fileId: string;
  fileName: string;
  initialHtml: string;
  canEdit: boolean;
  user: { id: string; name: string; email?: string; profilePicture?: string };
  /** Persist a .docx (base64). Throws FileChangedError on a version conflict. */
  onSaveDocx: (base64Docx: string) => Promise<void>;
  /** Reload the file (after a conflict). */
  onReload?: () => void;
}

const readSeen = () => {
  try { return localStorage.getItem(FILE_EDIT_WARNING_KEY) === '1'; } catch { return false; }
};

const FileCollaborativeEditor: React.FC<Props> = ({
  fileId, fileName, initialHtml, canEdit, user, onSaveDocx, onReload,
}) => {
  const { t } = useTranslation();
  const [warningSeen, setWarningSeen] = useState(readSeen);
  const [conflict, setConflict] = useState(false);

  const dismissWarning = () => {
    setWarningSeen(true);
    try { localStorage.setItem(FILE_EDIT_WARNING_KEY, '1'); } catch { /* best effort */ }
  };

  const save = useCallback(async (html: string) => {
    if (conflict) return;
    const docx = await htmlToDocx(html, fileName);
    try {
      await onSaveDocx(await blobToBase64(docx));
    } catch (e) {
      if (e instanceof FileChangedError || (e as any)?.code === 'FILE_CHANGED') {
        setConflict(true);
      }
      throw e; // the editor shows "Failed" for this save
    }
  }, [conflict, fileName, onSaveDocx]);

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="file-collaborative-editor">
      {canEdit && !warningSeen && (
        <div
          role="note"
          className="flex flex-shrink-0 items-start gap-2 border-b border-hairline-soft bg-warning-50 px-4 py-2 text-sm text-warning-900 dark:bg-warning-900/20 dark:text-warning-200"
          data-testid="file-edit-warning"
        >
          <PiInfoLight className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
          <p className="flex-1">
            {t('Editing here simplifies some Word formatting - tracked changes, fields, headers and footers. Every save is a new version you can restore from Version history, and Download gives you the file for full editing in Word.')}
          </p>
          <button type="button" onClick={dismissWarning} aria-label={t('Got it')} className="rounded p-0.5 hover:bg-warning-100 dark:hover:bg-warning-900/40">
            <PiXLight className="h-4 w-4" />
          </button>
        </div>
      )}
      {conflict && (
        <div
          role="alert"
          className="flex flex-shrink-0 items-center gap-3 border-b border-hairline-soft bg-danger-50 px-4 py-2 text-sm text-danger-800 dark:bg-danger-900/20 dark:text-danger-200"
          data-testid="file-edit-conflict"
        >
          <p className="flex-1">{t('Someone else saved this file after you opened it. Reload to see their changes - your last edits here were not saved.')}</p>
          {onReload && (
            <button type="button" onClick={onReload} className="font-medium underline underline-offset-2">
              {t('Reload')}
            </button>
          )}
        </div>
      )}
      <div className="min-h-0 flex-1">
        <CollaborativeDocumentEditor
          // The provider uses this as its PubSub GROUP, so it must be per
          // file - a shared constant would put every open file in one room.
          // (Before COLLAB_EDITING_ENABLED goes on, the backend must check
          // file access when granting this group.)
          matterUuid={`file-${fileId}`}
          documentId={fileId}
          initialHtml={initialHtml}
          collabEnabled={COLLAB_EDITING_ENABLED}
          user={user}
          readOnly={!canEdit || conflict}
          readOnlyReason={conflict ? t('A newer version was saved - reload to edit') : !canEdit ? t('You can view this file but not edit it') : undefined}
          placeholder={t('Start typing…')}
          autoSaveInterval={5000}
          onSave={(html) => save(html)}
        />
      </div>
    </div>
  );
};

export default FileCollaborativeEditor;
